import { UrlHandler } from "@/services/utils/UrlHandler.js";
import { LayerService } from "@/services/layers/layerService";

// Los atributos censales pueden llegar con distinto casing según el
// proveedor (GeoServer/ArcGIS): se busca sin distinguir mayúsculas en vez
// de asumir siempre minúsculas.
export const getProp = (props, key) => {
    if (props[key] !== undefined) return props[key];
    const lowerKey = key.toLowerCase();
    const foundKey = Object.keys(props).find((k) => k.toLowerCase() === lowerKey);
    return foundKey !== undefined ? props[foundKey] : "";
};

const buildGeoserverFilter = (geometry, populationMethod) => {
    const operator = populationMethod === "point" ? "WITHIN" : "INTERSECTS";
    const ring = geometry.coordinates[0];
    const first = ring[0];
    const last = ring[ring.length - 1];
    const closedRing = (first[0] !== last[0] || first[1] !== last[1]) ? [...ring, first] : ring;
    const coordStr = closedRing.map((c) => `${c[0]} ${c[1]}`).join(",");
    return `${operator}(the_geom, SRID=3857;POLYGON((${coordStr})))`;
};

const fetchGeoServerFeatures = async (layer, geometry, populationMethod) => {
    const workspace = LayerService.getGeoserverWorkspace(layer);
    const datastore = LayerService.getGeoserverDataStore(layer);
    const url = UrlHandler.getBaseUrl(layer) + "/wfs";
    const params = new URLSearchParams({
        service: "WFS",
        version: "2.0.0",
        request: "GetFeature",
        typeNames: `${workspace}:${datastore}`,
        outputFormat: "application/json",
        CQL_FILTER: buildGeoserverFilter(geometry, populationMethod),
    });
    const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
    });
    if (!response.ok) return [];
    const data = await response.json();
    return data.features || [];
};

const fetchArcgisFeatures = async (layer, geometry) => {
    const url = UrlHandler.getArcgisUrl(layer) + "/query";
    const esriGeometry = {
        rings: geometry.coordinates,
        spatialReference: { wkid: 3857 },
    };
    const baseParams = {
        f: "json",
        where: "1=1",
        geometry: JSON.stringify(esriGeometry),
        geometryType: "esriGeometryPolygon",
        spatialRel: "esriSpatialRelIntersects",
        inSR: "3857",
        outFields: "*",
        outSR: "3857",
        returnGeometry: "false",
    };
    const features = [];
    const MAX_PAGES = 50;
    for (let page = 0; page < MAX_PAGES; page++) {
        const params = new URLSearchParams(baseParams);
        if (features.length) params.set("resultOffset", String(features.length));
        const response = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/x-www-form-urlencoded" },
            body: params.toString(),
        });
        if (!response.ok) break;
        const data = await response.json();
        if (data.error) break;
        const pageFeatures = data.features || [];
        features.push(...pageFeatures);
        if (!data.exceededTransferLimit || !pageFeatures.length) break;
    }
    return features;
};

// Trae los features de una capa censal que intersectan/están dentro de una
// geometría (un anillo del geoproceso). WITHIN para el método "point" replica
// exacto la regla real del backend geoprocess (geoserver_provider.py); ArcGIS
// no distingue por método, siempre INTERSECTS.
export const fetchPopulationFeatures = async (layer, geometry, populationMethod) => {
    const provider = layer && layer.provider && layer.provider.name;
    if (provider === "geoserver") return fetchGeoServerFeatures(layer, geometry, populationMethod);
    if (provider === "arcgis") return fetchArcgisFeatures(layer, geometry);
    return [];
};
